
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Sun, Moon, Monitor, ChevronRight } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { usePermessiStore } from '../../stores/permessi.store';
import { useThemeStore } from '../../stores/theme.store';
import { theme } from '../../lib/theme';
import { getStoredAvatar } from '../../lib/avatar';
import { navSections, visibleNavSections } from '../../lib/navConfig';
import NotificationBell from './NotificationBell';
import SessionTimerBadge from './SessionTimerBadge';
import PresenceStatusBadge from './PresenceStatusBadge';

const SUBROUTE_LABELS = {
  '/pratiche/pneumatici': 'Authority Pneumatici LTR',
  '/pratiche/fermo-tecnico': 'Fermo Tecnico ARVAL LTR',
  '/pratiche/authority-bodywork': 'Authority Meccanica e Carrozzeria LTR',
  '/admin/archivio/pneumatici': 'Authority Pneumatici LTR',
  '/admin/archivio/fermo-tecnico': 'Fermo Tecnico ARVAL LTR',
};

function resolveBreadcrumbs(pathname, sections = navSections) {
  const canHome = sections.some((s) => s.items.some((i) => i.to === '/home'));
  const homeRoot = canHome ? '/home' : undefined;

  if (!pathname || pathname === '/' || pathname === '/home') {
    return [{ label: 'Home', to: homeRoot }];
  }
  if (pathname === '/profilo') {
    return [{ label: 'Home', to: homeRoot }, { label: 'Profilo Personale' }];
  }
  if (pathname === '/notifiche') {
    return [{ label: 'Home', to: homeRoot }, { label: 'Notifiche' }];
  }
  if (pathname.startsWith('/admin/operatori/')) {
    const adminSection = sections.find((s) => s.label === 'Amministrazione' || s.label === 'IAM');
    return [
      { label: adminSection?.label || 'Amministrazione', to: adminSection?.items[0]?.to },
      { label: 'Dettaglio Operatore' },
    ];
  }
  if (pathname === '/bundle') {
    return [{ label: 'Operatore', to: '/dashboard' }, { label: 'Bundle Giornaliero' }];
  }

  for (const section of sections) {
    const sectionRoot = section.items[0]?.to;
    for (const item of section.items) {
      if (Array.isArray(item.schede)) {
        // Sort schede by path length descending so '/helpdesk/board' matches before '/helpdesk'
        const sortedSchede = [...item.schede].sort((a, b) => b.to.length - a.to.length);
        const matchedScheda = sortedSchede.find(
          (s) => pathname === s.to || pathname.startsWith(`${s.to}/`)
        );
        if (matchedScheda) {
          const trail = [
            { label: section.label, to: sectionRoot },
            { label: item.label, to: item.to },
          ];
          if (matchedScheda.label.toLowerCase() !== item.label.toLowerCase()) {
            trail.push({ label: matchedScheda.label, to: matchedScheda.to });
          }
          if (pathname !== matchedScheda.to) {
            const subLabel = SUBROUTE_LABELS[pathname];
            if (subLabel) {
              trail.push({ label: subLabel });
            } else if (pathname.startsWith('/helpdesk/tickets/')) {
              const ticketKey = decodeURIComponent(pathname.slice('/helpdesk/tickets/'.length));
              trail.push({ label: ticketKey });
            } else if (pathname === '/helpdesk/new') {
              trail.push({ label: 'Nuovo Ticket' });
            } else if (pathname.startsWith('/pratiche/')) {
              const slug = pathname.slice('/pratiche/'.length).replace(/-/g, ' ');
              trail.push({ label: slug.charAt(0).toUpperCase() + slug.slice(1) });
            } else if (pathname.startsWith('/admin/archivio/')) {
              const slug = pathname.slice('/admin/archivio/'.length).replace(/-/g, ' ');
              trail.push({ label: slug.charAt(0).toUpperCase() + slug.slice(1) });
            } else if (pathname.startsWith('/effeisbuck/posts/')) {
              trail.push({ label: 'Dettaglio Post' });
            } else if (pathname.startsWith('/effeisbuck/profilo/')) {
              trail.push({ label: 'Profilo Utente' });
            }
          }
          return trail;
        }
      }

      if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        if (section.label.toLowerCase() === item.label.toLowerCase()) {
          return [{ label: item.label, to: item.to }];
        }
        return [
          { label: section.label, to: sectionRoot },
          { label: item.label, to: item.to },
        ];
      }
    }
  }

  return [{ label: 'Home', to: homeRoot }];
}

export default function TopBar({ onMenuToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { permessi, catalogo, stato: statoPermessi } = usePermessiStore();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const ruolo = user?.ruolo || 'OPERATOR';
  const [avatarUrl, setAvatarUrl] = useState(null);

  const cycleMode = () => {
    const order = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(mode) + 1) % order.length];
    setMode(next);
  };
  const ModeIcon = mode === 'dark' ? Moon : mode === 'system' ? Monitor : Sun;
  const modeLabel = mode === 'dark' ? 'Scuro' : mode === 'system' ? 'Sistema' : 'Chiaro';

  useEffect(() => {
    const loadAvatar = () => {
      if (user?.id) {
        setAvatarUrl(getStoredAvatar(user.id));
      }
    };
    loadAvatar();
    window.addEventListener('wfm-avatar-changed', loadAvatar);
    return () => window.removeEventListener('wfm-avatar-changed', loadAvatar);
  }, [user?.id]);

  const accesso = statoPermessi === 'pronto' ? { permessi, catalogo } : null;
  const sections = visibleNavSections(user, accesso);
  const crumbs = resolveBreadcrumbs(location.pathname, sections);
  const roleMeta = theme.roleBadgeColors[ruolo] || theme.roleBadgeColors.OPERATOR;

  return (
    // sticky banner; pad the top by the iOS safe-area inset so the hamburger +
    // breadcrumb sit BELOW the status bar/notch (status bar is translucent in
    // the installed PWA). Solid white + shadow-md keeps it bold/legible over
    // scrolling content. z-30 stays under the sidebar overlay (z-40).
    <header
      className="sticky top-0 shrink-0 z-30 bg-bg-white border-b border-border"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        backgroundColor: 'var(--wfm-bg-white)',
        borderColor: 'var(--wfm-border)',
        boxShadow: 'var(--wfm-shadow-sm)',
      }}
    >
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuToggle}
          aria-label="Apri menu"
          className="p-2 -ml-1 text-text-mid hover:text-text-main rounded-lg hover:bg-bg-light active:bg-bg-light lg:hidden shrink-0"
        >
          <Menu size={24} />
        </button>

        <nav aria-label="Breadcrumb" className="flex items-center text-[13px] min-w-0">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <React.Fragment key={`${crumb.label}-${i}`}>
                {i > 0 && (
                  <ChevronRight
                    size={14}
                    strokeWidth={1.85}
                    className="mx-1.5 shrink-0 hidden sm:inline"
                    style={{ color: 'var(--wfm-text-light)' }}
                  />
                )}
                {isLast || !crumb.to ? (
                  <span
                    className="font-semibold truncate"
                    style={{ color: 'var(--wfm-text-main)' }}
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.to}
                    className="hidden sm:inline font-medium no-underline transition-colors hover:underline truncate"
                    style={{ color: 'var(--wfm-text-light)' }}
                  >
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <PresenceStatusBadge />
        <SessionTimerBadge />
        <button
          type="button"
          onClick={cycleMode}
          aria-label={`Modalità aspetto: ${modeLabel}. Clicca per cambiare`}
          title={`Aspetto: ${modeLabel} (clicca per cambiare)`}
          className="p-2 text-text-mid hover:text-text-main rounded-lg hover:bg-bg-light transition-colors"
        >
          <ModeIcon size={18} />
        </button>
        <NotificationBell />

        <span
          className="hidden sm:inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border whitespace-nowrap shrink-0"
          style={{
            backgroundColor: roleMeta.bg,
            color: roleMeta.text,
            borderColor: roleMeta.border
          }}
        >
          {theme.roleLabels[ruolo] || ruolo}
        </span>

        <button
          onClick={() => navigate('/profilo')}
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-shadow"
          style={{ backgroundColor: avatarUrl ? 'transparent' : 'var(--wfm-primary-fill)' }}
          title="Vai al profilo"
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <>{user?.nome?.[0]}{user?.cognome?.[0]}</>
          )}
        </button>
      </div>
      </div>
    </header>
  );
}
